import { useEffect, useId, useRef, useState } from "react"

// The builder's dropdown. A listbox rather than a native <select>, so it
// matches the Customize design panel: the same caret, turning over when open,
// and a list that drops down over whatever sits below instead of the OS's
// floating picker. The destination kind picker and the dashboard's Look use it.
//
// `options` are objects with an `id` and a `label`; onChange gets the chosen
// option, and only when it differs from `value`. `className` styles the
// button, so each caller matches the fields beside it. `fill` stretches it to
// its container and lets a long list scroll; otherwise it is as wide as its
// widest label, so nothing beside it shifts when the choice changes.
export default function Listbox({ options, value, onChange, disabled, label, fill = false, className = "", buttonProps = {} }) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)
  const listId = useId()
  const current = options.find((o) => o.id === value) || options[0]

  useEffect(() => {
    if (!open) return
    function onDoc(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  // Keep the keyboard's option in view when a long list scrolls.
  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: "nearest" })
  }, [open, active])

  function openList() {
    setActive(Math.max(0, options.findIndex((o) => o.id === value)))
    setOpen(true)
  }

  function choose(o) {
    setOpen(false)
    if (o.id !== value) onChange(o)
    else buttonRef.current?.focus()
  }

  function onKeyDown(e) {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); openList() }
      return
    }
    const last = options.length - 1
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(last, i + 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)) }
    else if (e.key === "Home") { e.preventDefault(); setActive(0) }
    else if (e.key === "End") { e.preventDefault(); setActive(last) }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(options[active]) }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false) }
    else if (e.key === "Tab") setOpen(false)
  }

  const shown = open && !disabled

  return (
    <div ref={rootRef} className={"relative " + (fill ? "w-full" : "shrink-0")}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-activedescendant={shown ? `${listId}-${active}` : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        {...buttonProps}
        className={"h-full inline-flex items-center justify-between gap-3 text-left cursor-pointer disabled:cursor-not-allowed " + (fill ? "w-full " : "") + className}
      >
        {fill ? (
          <span className="min-w-0 truncate">{current.label}</span>
        ) : (
          // Every label stacked in one grid cell, only the current one visible,
          // so the button is as wide as its widest label.
          <span className="grid">
            {options.map((o) => (
              <span key={o.id} aria-hidden={o.id !== current.id} className={"col-start-1 row-start-1 " + (o.id === current.id ? "" : "invisible")}>
                {o.label}
              </span>
            ))}
          </span>
        )}
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          aria-hidden="true"
          className={"shrink-0 text-coal dark:text-cloud transition-transform " + (shown ? "rotate-180" : "")}
          fill="currentColor"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {shown && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          className={
            "absolute left-0 top-full mt-1 z-30 py-1 bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-lg " +
            (fill ? "w-full max-h-72 overflow-y-auto" : "min-w-full overflow-hidden")
          }
        >
          {options.map((o, i) => (
            <li
              key={o.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.id === current.id}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
              className={
                "px-4 py-2 text-sm cursor-pointer whitespace-nowrap flex items-center justify-between gap-4 text-ink dark:text-cloud " +
                (i === active ? "bg-cloud dark:bg-slate" : "") +
                (o.id === current.id ? " font-semibold" : "")
              }
            >
              <span className="min-w-0 truncate">{o.label}</span>
              {o.id === current.id && (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-voltage-dark dark:text-voltage-light">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
